import '../webpack-dynamic-config-change';
import React from 'react';
import ReactDOM from 'react-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Provider } from 'react-redux';
import { store } from 'reducers';
import Widgets from 'components/Widgets';
import ModalProvider from 'components/ui/Modals/ModalProvider';
import { initSentry, setUserContext as setSentryUserContext } from 'utils/sentry';
import { BrowserRouter } from 'react-router-dom';
import { BladeProvider, ToastContainer } from '@razorpay/blade/components';
import { bladeTheme } from '@razorpay/blade/tokens';
import { getThemePreference } from 'utils/getThemePreference';
import { WidgetThemeContextProvider } from 'WidgetThemeContextProvider';
import '@razorpay/blade/fonts.css';
import { RouterNavigationProvider } from 'context/RouterNavigationProvider';

initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      retryOnMount: false,
    },
  },
});

interface WidgetMessage {
  elementType: string;
  elementId: string;
  props: any;
}

declare global {
  interface Window {
    payroll: {
      setSentryUserContext: typeof setSentryUserContext;
      toggleLeftSidebar: () => void;
      isWidgetLoaded: boolean;
    };
  }
}

window.payroll = {
  setSentryUserContext,
  toggleLeftSidebar: () => {},
  isWidgetLoaded: true,
};

window.addEventListener('message', (event) => {
  if (event.origin !== window.location.origin || !event.data.elementType) return;

  if (!event.data.elementId && !event.data.elementType) {
    return;
  }

  const { elementType, props, elementId }: WidgetMessage = event.data;

  if (document.getElementById(elementId)) {
    ReactDOM.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <RouterNavigationProvider>
              <Provider store={store}>
                <WidgetThemeContextProvider initialTheme={getThemePreference()}>
                  <BladeProvider themeTokens={bladeTheme} colorScheme={getThemePreference()}>
                    <ToastContainer />
                    <ModalProvider>
                      <Widgets elementType={elementType} props={props} />
                    </ModalProvider>
                  </BladeProvider>
                </WidgetThemeContextProvider>
              </Provider>
            </RouterNavigationProvider>
          </BrowserRouter>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </React.StrictMode>,
      document.getElementById(elementId),
    );
  }
});

window.postMessage({ message: 'payroll.widget.loaded' }, window.location.origin);
